import * as React from "react";
import { ArrowLeft } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * Where an in-app deep link came from. Set as the router `state.from` on a
 * cross-view navigation (e.g. a booking-page calendar → the Meetings calendar)
 * so the destination can offer a natural way back.
 */
export type NavFrom = {
  label: string;
  /**
   * Where "back" actually is. Omitted, back means one history entry, which is right for an
   * in-app hop. Set it when the trip leaves the SPA and comes back on a fresh page load (a
   * calendar's OAuth redirect), because by then there is no history entry to pop.
   */
  to?: string;
};

/**
 * A quiet "← Back to {origin}" breadcrumb that only appears when you arrived via
 * an in-app deep link (i.e. `location.state.from` is set). Clicking it pops one
 * history entry, so hopping across several bookings/meetings and back unwinds the
 * whole chain one step at a time — the browser Back button does the same thing.
 *
 * <p>It renders nothing on a page reached directly (a pasted URL, a fresh tab),
 * so it never strands you by leaving the app.</p>
 *
 * <p>A page may pass {@code from} itself instead of relying on router state, for the
 * one case router state cannot cover: an errand that leaves the app and returns on a
 * fresh page load, where the state (and the history entry) are gone.</p>
 */
export function BackBar({
  current,
  from: given,
}: {
  current?: React.ReactNode;
  from?: NavFrom | null;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const from = given ?? (location.state as { from?: NavFrom } | null)?.from;
  if (!from) return null;
  return (
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      <button
        type="button"
        onClick={() => (from.to ? navigate(from.to) : navigate(-1))}
        title={`Back to ${from.label}`}
        className="group inline-flex items-center gap-1.5 rounded-full border border-border px-2.5 py-1 font-medium transition hover:bg-muted hover:text-foreground"
      >
        <ArrowLeft className="h-3.5 w-3.5 transition-transform group-hover:-translate-x-0.5" />
        Back to {from.label}
      </button>
      {current != null && (
        <span className="truncate">
          <span aria-hidden="true">▸ </span>
          {current}
        </span>
      )}
    </div>
  );
}
